import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { MapPin, Clock, Briefcase, Users, Heart, Zap, Award } from 'lucide-react';
import { careers } from '../mock';

export const Careers = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-light tracking-tight mb-6">
              Join Our Team
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 font-light">
              Build your career with talented professionals who are passionate about technology
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-4">
              Why Work With Us?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Zap className="text-blue-600" size={32} />
                </div>
                <h3 className="text-xl font-medium mb-3">Innovation First</h3>
                <p className="text-gray-600">
                  Work on cutting-edge projects using the latest technologies and methodologies.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="text-teal-600" size={32} />
                </div>
                <h3 className="text-xl font-medium mb-3">Great Team Culture</h3>
                <p className="text-gray-600">
                  Collaborate with talented individuals in a supportive and inclusive environment.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Award className="text-purple-600" size={32} />
                </div>
                <h3 className="text-xl font-medium mb-3">Growth Opportunities</h3>
                <p className="text-gray-600">
                  Continuous learning, professional development, and career advancement.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Perks Section */}
      <section className="py-16 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-light text-center mb-12">Perks & Benefits</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                'Competitive salary packages',
                'Health insurance coverage',
                'Flexible work arrangements',
                'Remote work options',
                'Professional development budget',
                'Latest tech equipment',
                'Team building activities',
                'Work-life balance'
              ].map((perk, idx) => (
                <div key={idx} className="flex items-center space-x-3">
                  <Heart size={18} className="text-blue-600 flex-shrink-0" />
                  <span className="text-gray-700">{perk}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-4">
              Open Positions
            </h2>
            <p className="text-xl text-gray-600">Find your next opportunity</p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {careers.map((job) => (
              <Card key={job.id} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-2xl font-medium mb-2">{job.title}</h3>
                          <div className="flex flex-wrap gap-3 mb-4">
                            <Badge variant="outline" className="flex items-center space-x-1">
                              <Briefcase size={14} />
                              <span>{job.department}</span>
                            </Badge>
                            <Badge variant="outline" className="flex items-center space-x-1">
                              <MapPin size={14} />
                              <span>{job.location}</span>
                            </Badge>
                            <Badge variant="outline" className="flex items-center space-x-1">
                              <Clock size={14} />
                              <span>{job.type}</span>
                            </Badge>
                          </div>
                        </div>
                      </div>

                      <p className="text-gray-600 mb-4">{job.description}</p>

                      <div>
                        <h4 className="font-medium mb-2">Requirements:</h4>
                        <ul className="space-y-1">
                          {job.requirements.map((req, idx) => (
                            <li key={idx} className="text-sm text-gray-600 flex items-start">
                              <span className="mr-2">•</span>
                              <span>{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="lg:ml-6">
                      <Button className="w-full lg:w-auto bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200 hover:scale-105">
                        Apply Now
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};